import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { devDebug } from "@/lib/debug";

const AuthContext = createContext(null);

// Read a browser cookie by name (JS-readable cookies only).
function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((c) => c.startsWith(name + "="));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

// Read the access_expires_at hint cookie (unix seconds) set by the backend.
// Returns null if missing / unparseable.
function readAccessExpiresMs() {
  const raw = readCookie("access_expires_at");
  if (!raw) return null;
  const secs = parseInt(raw, 10);
  return Number.isFinite(secs) && secs > 0 ? secs * 1000 : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null while loading, false if unauth, obj if auth
  const [loading, setLoading] = useState(true);
  // Timer that force-logouts when the 4h session ceiling is hit, so users
  // don't stay on stale UI without an API call.
  const logoutTimerRef = useRef(null);
  // Guard so forceExpireLogout is idempotent even if both the timer and the
  // 401 interceptor race each other on the same expiry moment.
  const logoutFiredRef = useRef(false);

  const clearLogoutTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  // Schedule auto-logout at exactly the JWT expiry. Called after login and
  // on mount when a live session is detected. Safe to call multiple times —
  // any previously-scheduled timer is cleared first.
  const scheduleAutoLogout = () => {
    clearLogoutTimer();
    const expiresMs = readAccessExpiresMs();
    if (!expiresMs) return;
    const remaining = expiresMs - Date.now();
    if (remaining <= 0) {
      // Already expired — force logout immediately.
      forceExpireLogout();
      return;
    }
    logoutTimerRef.current = setTimeout(forceExpireLogout, remaining);
  };

  // Called when the client-side timer fires OR when we detect an already-
  // expired cookie on mount. Shows a toast + sends the user to /login.
  // Ordering: toast BEFORE server logout call, so the message renders even if
  // the axios interceptor's 401→redirect fires first.
  const forceExpireLogout = async () => {
    if (logoutFiredRef.current) return; // idempotent
    logoutFiredRef.current = true;
    clearLogoutTimer();
    toast.info("Sesi Anda berakhir (batas 4 jam). Silakan login ulang.", { duration: 8000 });
    setUser(false);
    try { await api.post("/auth/logout"); }
    catch (err) { devDebug("[auto-logout] server clear failed:", err?.message || err); }
    // Small delay so the toast has a chance to render before we navigate.
    if (!window.location.pathname.startsWith("/login")) {
      setTimeout(() => { window.location.href = "/login"; }, 250);
    }
  };

  // On mount, ask the server "who am I?" using the httpOnly cookie (if any).
  // A 401 means we're not logged in; anything else that fails treats us as
  // unauthenticated as well.
  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => { setUser(r.data); scheduleAutoLogout(); })
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
    return clearLogoutTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password, otp = null) => {
    // Backend sets httpOnly access_token + JS-readable csrf_token + access_expires_at cookies.
    // If the account has MFA enrolled and no OTP was passed, backend replies
    // `{ mfa_required: true }` (no token issued). Caller must re-invoke
    // with the OTP the user just typed. Signalled via a thrown MfaRequired
    // sentinel so the calling form can render a second-factor input.
    logoutFiredRef.current = false; // fresh session — allow future auto-logout
    const payload = otp ? { email, password, otp } : { email, password };
    const { data } = await api.post("/auth/login", payload);
    if (data.mfa_required) {
      const err = new Error("MFA_REQUIRED");
      err.mfaRequired = true;
      throw err;
    }
    setUser(data.user);
    scheduleAutoLogout();
    return data.user;
  };

  const logout = async () => {
    // Clear server-side auth cookies. Fire-and-forget: if the request fails
    // we still send the UI to /login (e.g., token already expired).
    clearLogoutTimer();
    try {
      await api.post("/auth/logout");
    } catch (err) {
      devDebug("[logout] server-side clear failed:", err?.message || err);
    }
    setUser(false);
  };

  // Re-fetch the current user (used after MFA enrolment to clear the gate).
  const refreshUser = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch (err) {
      devDebug("[refreshUser] failed:", err?.message || err);
      return null;
    }
  };

  // Stable context value to keep memoized consumers from re-rendering unnecessarily.
  const ctxValue = useMemo(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading],
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
